import { useEffect, useMemo, useState } from 'react';
import { useApp } from '../app/store.ts';
import { deriveTitle } from '../data/markdown.ts';
import { searchIds } from '../search/client.ts';

type Entry = { id: string; label: string; hint?: string; run: () => void };

type Props = { onClose: () => void; onOpenSettings: () => void };

/**
 * Ein Feld, zwei Gruppen, immer dieselbe Reihenfolge: Befehle über Notizen. Hier landet
 * alles, was aus der Kopfzeile verbannt wurde — Papierkorb, Dichte, Sperren, Ordner.
 */
export function Palette({ onClose, onOpenSettings }: Props): JSX.Element {
  const {
    notes, folders, activeId, showTrash, density,
    create, togglePin, trash, markVersion, open, setShowTrash, setFolderFilter, toggleDensity, leave,
    setShowTodos,
  } = useApp();
  const [text, setText] = useState('');
  const [cursor, setCursor] = useState(0);
  const [hits, setHits] = useState<string[]>([]);

  useEffect(() => {
    let alive = true;
    const timer = setTimeout(() => {
      if (!text.trim()) {
        setHits([]);
        return;
      }
      void searchIds(text, 12).then((result) => {
        if (alive) setHits(result?.ids ?? []);
      });
    }, 100);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [text]);

  const commands = useMemo<Entry[]>(() => {
    const list: Entry[] = [
      { id: 'new', label: 'Neue Notiz', hint: '⌘N', run: () => void create() },
      { id: 'todos', label: 'Erinnerungen', hint: 'abhakbare Liste', run: () => setShowTodos(true) },
      // Weit oben, weil es auf dem Telefon der einzige Weg dorthin ist: ohne Tastatur
      // gibt es kein ⌘K, und danach suchen zu müssen ist keine Bedienung.
      { id: 'settings', label: 'Einstellungen', run: onOpenSettings },
    ];
    if (activeId) {
      list.push(
        { id: 'pin', label: 'Anpinnen oder lösen', run: () => void togglePin(activeId) },
        { id: 'mark', label: 'Version markieren', hint: 'bleibt dauerhaft', run: () => void markVersion(activeId) },
        { id: 'del', label: 'In den Papierkorb', run: () => void trash(activeId) },
      );
    }
    for (const folder of folders) {
      list.push({
        id: `folder-${folder.id}`,
        label: `Ordner: ${folder.name}`,
        run: () => setFolderFilter(folder.id),
      });
    }
    list.push(
      { id: 'all', label: 'Alle Ordner zeigen', run: () => setFolderFilter(null) },
      { id: 'trash', label: showTrash ? 'Papierkorb verlassen' : 'Papierkorb anzeigen', run: () => setShowTrash(!showTrash) },
      {
        id: 'density',
        label: density === 'compact' ? 'Dichte: luftig' : 'Dichte: kompakt',
        hint: '⌥D',
        run: () => toggleDensity(),
      },
      { id: 'lock', label: 'Vault sperren', hint: 'Schlüssel vergessen', run: () => leave() },
    );

    const needle = text.trim().toLowerCase();
    const filtered = needle ? list.filter((entry) => entry.label.toLowerCase().includes(needle)) : list;
    // Leere Eingabe zeigt die fünf, die man am häufigsten braucht.
    return needle ? filtered : filtered.slice(0, 5);
  }, [text, activeId, showTrash, density, folders, create, togglePin, markVersion, trash, setFolderFilter, setShowTrash, toggleDensity, onOpenSettings, leave]);

  const found = hits
    .map((id) => notes.find((note) => note.id === id))
    .filter((note): note is NonNullable<typeof note> => note !== undefined)
    .map((note) => ({
      id: `note-${note.id}`,
      label: deriveTitle(note.content),
      hint: 'Notiz',
      run: () => open(note.id),
    }));

  const entries = [...commands, ...found];
  const selected = Math.min(cursor, Math.max(entries.length - 1, 0));

  useEffect(() => setCursor(0), [text]);

  const rows: JSX.Element[] = [];
  if (commands.length > 0) rows.push(<li key="g1" className="palette-group label">Befehle</li>);
  entries.forEach((entry, position) => {
    if (position === commands.length && found.length > 0) {
      rows.push(<li key="g2" className="palette-group label">Notizen</li>);
    }
    rows.push(
      <li key={entry.id}>
        <button
          className={position === selected ? 'palette-row palette-on' : 'palette-row'}
          onMouseEnter={() => setCursor(position)}
          onClick={() => {
            entry.run();
            onClose();
          }}
        >
          <span>{entry.label}</span>
          {entry.hint && <em>{entry.hint}</em>}
        </button>
      </li>,
    );
  });

  return (
    <div className="palette-backdrop" onClick={onClose} role="presentation">
      <div className="palette" onClick={(event) => event.stopPropagation()} role="presentation">
        <input
          autoFocus
          className="palette-input"
          placeholder="Suchen oder Befehl …"
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') onClose();
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setCursor((value) => Math.min(value + 1, entries.length - 1));
            }
            if (event.key === 'ArrowUp') {
              event.preventDefault();
              setCursor((value) => Math.max(value - 1, 0));
            }
            if (event.key === 'Enter') {
              event.preventDefault();
              entries[selected]?.run();
              onClose();
            }
          }}
        />
        <ul className="palette-list">
          {rows}
          {entries.length === 0 && <li className="palette-empty">Nichts gefunden.</li>}
        </ul>
      </div>
    </div>
  );
}
